import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';

/**
 * CRT EFFECT
 *
 * Opt-in fullscreen overlay that adds scanlines, screen curvature,
 * and a subtle chromatic aberration to simulate an old CRT monitor.
 * Toggled via localStorage `wipe_crt` and the `wipe-crt-toggle`
 * window event.
 *
 * Everything is pointer-events: none so the overlay never catches
 * input. Render at zIndex 2000 so it paints above all content but
 * below modals/snackbars (which sit at 1300-1400).
 */

const STORAGE_KEY = 'wipe_crt';

export function setCRTEnabled(on: boolean) {
  try { localStorage.setItem(STORAGE_KEY, on ? '1' : '0'); } catch { /* ignore */ }
  window.dispatchEvent(new CustomEvent('wipe-crt-toggle', { detail: { on } }));
}

export function getCRTEnabled(): boolean {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
}

function CRTEffect() {
  const [on, setOn] = useState<boolean>(() => getCRTEnabled());

  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ on: boolean }>;
      setOn(!!ce.detail?.on);
    };
    window.addEventListener('wipe-crt-toggle', handler);
    return () => window.removeEventListener('wipe-crt-toggle', handler);
  }, []);

  if (!on) return null;

  return (
    <Box
      aria-hidden
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 2000,
        pointerEvents: 'none',
        mixBlendMode: 'multiply',
        // Scanlines + very subtle vignette
        background: `
          repeating-linear-gradient(
            to bottom,
            rgba(255,255,255,0.03) 0px,
            rgba(255,255,255,0.03) 1px,
            rgba(0,0,0,0.15) 1px,
            rgba(0,0,0,0.15) 3px
          ),
          radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.4) 100%)
        `,
        // Subtle animated flicker
        animation: 'crt-flicker 0.16s steps(2) infinite',
        '@keyframes crt-flicker': {
          '0%':   { opacity: 0.95 },
          '50%':  { opacity: 0.98 },
          '100%': { opacity: 0.95 },
        },
      }}
    />
  );
}

export default CRTEffect;
